/**
 * record_contacts rows for display: the icon a contact shows (a brand's for a
 * known profile URL), the text it reads as, and where it links.
 */
import { capitalize } from "./string"

/** A record_contacts row, as read — the columns display needs. */
export interface ContactRow {
  id?: number | string
  contact_type: string
  value: string
  label?: string | null
  is_primary?: boolean | null
}

/** The four contact kinds, in picker order, with each one's icon. */
export const CONTACT_KINDS = [
  { key: "email", label: "Email", icon: "envelope" },
  { key: "phone", label: "Phone", icon: "phone" },
  { key: "url", label: "Link", icon: "globe" },
  { key: "location", label: "Location", icon: "map-marker-alt" },
] as const

export type ContactKind = (typeof CONTACT_KINDS)[number]["key"]

// Brand icons for profile URLs (domain -> [prefix, icon]).
const BRAND_ICONS: Record<string, [string, string]> = {
  "github.com": ["fab", "github"],
  "twitter.com": ["fab", "twitter"],
  "x.com": ["fab", "twitter"],
  "linkedin.com": ["fab", "linkedin"],
  "mastodon.social": ["fab", "mastodon"],
  "news.ycombinator.com": ["fab", "hacker-news"],
  "stackoverflow.com": ["fab", "stack-overflow"],
  "slack.com": ["fab", "slack"],
  "bsky.app": ["fab", "bluesky"],
}

function parseUrl(value: string): URL | null {
  try {
    return new URL(value)
  } catch {
    return null
  }
}

/** The icon for a contact kind. */
export const contactKindIcon = (kind: string): string =>
  CONTACT_KINDS.find((k) => k.key === kind)?.icon ?? "circle-question"

/** A contact's icon: a brand's for a known profile URL, else its kind's. */
export function contactIcon(
  contact: Pick<ContactRow, "contact_type" | "value">
): string | [string, string] {
  if (contact.contact_type === "url" && contact.value) {
    const host = parseUrl(contact.value)?.hostname
    if (host) {
      for (const [domain, icon] of Object.entries(BRAND_ICONS)) {
        if (host === domain || host.endsWith(`.${domain}`)) return icon
      }
    }
  }
  return contactKindIcon(contact.contact_type)
}

/** The text a contact reads as: an email or phone as is, a URL by its handle,
 *  a location by its label or a shortened address. */
export function contactText(contact: Pick<ContactRow, "contact_type" | "value" | "label">): string {
  const { contact_type, value } = contact
  const kindLabel = CONTACT_KINDS.find((k) => k.key === contact_type)?.label ?? ""
  if (!value) return kindLabel
  if (contact_type === "url") {
    const handle = parseUrl(value)?.pathname.split("/").filter(Boolean).at(-1)
    if (handle) return capitalize(handle)
  }
  if (contact_type === "location") {
    return contact.label || (value.length > 20 ? `${value.slice(0, 15)}…` : value)
  }
  return value
}

/** Where a contact links: mail, call, the page, or the map. */
export function contactHref(contact: Pick<ContactRow, "contact_type" | "value">): string | null {
  const { contact_type, value } = contact
  if (!value) return null
  switch (contact_type) {
    case "email":
      return `mailto:${value}`
    case "phone":
      return `tel:${value}`
    case "url":
      return value
    case "location":
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`
    default:
      return null
  }
}
